import { Button, Checkbox, Divider, Grid, Typography } from '@mui/material';
import CommonDropzone from 'components/dropzone/CommonDropzone';
import React from 'react';
import { jbColors } from 'theme/jbColors';
import DocumentCaptureDate from './uploadContractButtonAndDialog/DocumentCaptureDate';
import DocumentDescription from './uploadContractButtonAndDialog/DocumentDescription';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { sendDocument } from 'thunks/productDocumentsThunk';
import DocumentDistributionLanguageSelect from './uploadContractButtonAndDialog/DocumentDistributionLanguageSelect';
import DocumentTypeSelect from './uploadContractButtonAndDialog/DocumentTypeSelect';

const containerStyle = {
  display: 'flex',
  alignItems: 'center',
  padding: 0,
  width: '500px',
  mt: 1,
};

type Props = {
  closeAction: () => void;
  taskId: string;
  hasLanguages?: boolean;
  formCodeList?: string[];
  hasCheckboxes?: boolean;
};

const UploadDocument = ({ closeAction, taskId, hasLanguages = false, formCodeList = [], hasCheckboxes }: Props) => {
  const [file, setFile] = React.useState<File | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const { selectedServiceAgreement } = useSelector((state: RootState) => state.serviceAgreements);
  const [distributionAssignee] = React.useState<string | null>(null);
  const [language, setLanguage] = React.useState<string>('English (EN)');
  const [formCode, setFormCode] = React.useState<string>('');
  const languages = ['English (EN)', 'German (DE)', 'French (FR)', 'Italian (IT)', 'Spanish (ES)'];
  const [terminationNoticeSignedCheckBox, setTerminationNoticeCheckBox] = React.useState<string>();
  const [terminationNoticeSentCheckBox, setTerminationNoticeSentCheckBox] = React.useState<string>();

  const handleUploadContract = (acceptedFiles: File) => {
    setFile(acceptedFiles);
  };

  const addDocument = async () => {
    if (!selectedServiceAgreement?.serviceAgreementId || !file) return;
    const parsedLanguage = language.split('(')[1].replace('(', '').replace(')', '');
    if (!terminationNoticeSentCheckBox && !terminationNoticeSignedCheckBox)
      dispatch(
        sendDocument(
          selectedServiceAgreement.serviceAgreementId,
          file,
          taskId,
          distributionAssignee ?? '',
          parsedLanguage,
          formCode,
        ),
      );

    if (terminationNoticeSentCheckBox && terminationNoticeSignedCheckBox) {
      dispatch(
        sendDocument(
          selectedServiceAgreement.serviceAgreementId,
          file,
          taskId,
          distributionAssignee ?? '',
          parsedLanguage,
          formCode,
          [terminationNoticeSentCheckBox, terminationNoticeSignedCheckBox],
        ),
      );
    }
    closeAction();
  };

  return (
    <Grid container sx={containerStyle}>
      <CommonDropzone setItem={handleUploadContract} />

      {file && <DocumentDescription setFile={setFile} file={file} />}

      {file && <DocumentTypeSelect formCode={formCode} formCodes={formCodeList} setFormCode={setFormCode} />}
      {file && hasLanguages && (
        <Grid container sx={{ borderRadius: 0 }}>
          <DocumentDistributionLanguageSelect language={language} languages={languages} setLanguage={setLanguage} />
        </Grid>
      )}
      {file && <DocumentCaptureDate file={file} />}
      <Divider
        sx={{
          backgroundColor: jbColors.core.stone[30],
          width: '100%',
          height: '1.5px',
          mt: 1,
        }}
      />
      {hasCheckboxes && (
        <Grid container>
          <Grid
            container
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Checkbox
              value="Termination notice signed."
              onChange={(v) =>
                v.target.checked
                  ? setTerminationNoticeCheckBox('Termination notice signed.')
                  : setTerminationNoticeCheckBox(undefined)
              }
            />
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>Termination notice signed.</Typography>
          </Grid>
          <Grid
            container
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Checkbox
              value="Termination notice sent by registered email to agent and broker."
              onChange={(v) =>
                v.target.checked
                  ? setTerminationNoticeSentCheckBox('Termination notice sent by registered email to agent and broker.')
                  : setTerminationNoticeSentCheckBox(undefined)
              }
            />
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              Termination notice sent by registered email to agent and broker.
            </Typography>
          </Grid>
        </Grid>
      )}
      <Grid container sx={[containerStyle, { justifyContent: 'flex-end' }]}>
        <Button
          id="upload-document-button"
          disabled={
            !file ||
            !formCode ||
            (hasCheckboxes && !terminationNoticeSentCheckBox) ||
            (hasCheckboxes && !terminationNoticeSignedCheckBox)
          }
          onClick={addDocument}
          sx={{
            mr: 0,
            color: 'white',
            backgroundColor: jbColors.core.reflexBlue[100],
            '&:hover': {
              color: jbColors.core.reflexBlue[100],
              fontWeight: 'bold',
            },
            '&:disabled': {
              color: 'white',
              opacity: 0.5,
            },
          }}
        >
          Add document
        </Button>
      </Grid>
    </Grid>
  );
};

export default UploadDocument;
